<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useAssistantStore } from '@/composition/assistant/use-assistant-store';

  /**
   * The light drawer host shared by the restaurant and user drawers (plan
   * §4.2). Vuetify's `v-navigation-drawer` stays the layout host (content
   * padding, clipping under the bar, overlay below Vuetify's mobile
   * breakpoint). It starts open only where it is permanent (Vuetify's
   * desktop widths, from 1264 px); below, it is an overlay the top bar's menu
   * button opens through the `showRestaurantMenu` document event.
   */
  const proxy = getCurrentInstance()!.proxy;
  const assistant = useAssistantStore();
  const mobile = computed(() => proxy.$vuetify.breakpoint.mobile);

  // Set before the first render: a drawer rendered open and closed later
  // stayed open over the page on small screens.
  const open = ref(!mobile.value);

  function toggle() {
    open.value = !open.value;
  }

  watch(mobile, (isMobile) => {
    open.value = !isMobile;
  });

  // Every opening (menu button, edge swipe, resize) is announced, so an
  // overlay «Asistente» panel can close: one layer at a time.
  watch(open, (isOpen) => {
    if (isOpen) document.dispatchEvent(new CustomEvent('restaurantMenuOpened'));
  });

  onMounted(() => document.addEventListener('showRestaurantMenu', toggle));
  onBeforeUnmount(() =>
    document.removeEventListener('showRestaurantMenu', toggle)
  );
</script>

<template>
  <v-navigation-drawer
    v-model="open"
    app
    clipped
    :color="'rgb(var(--c-surface))'"
    class="v2-shell-drawer hidden-print-only"
  >
    <!-- One scroller: the footer (QR, language) follows the nav on short
         screens and sits at the bottom on tall ones. -->
    <div
      class="drawer-body"
      :class="{ 'has-bubble-room': assistant.bubbleShown }"
    >
      <slot />
      <div v-if="$slots.footer" class="drawer-footer">
        <slot name="footer" />
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
  .v2-shell-drawer {
    @apply tw-text-neutral-800;
    border-right: 1px solid rgb(var(--c-neutral-200));

    :deep(.v-navigation-drawer__border) {
      display: none;
    }

    // Vuetify sets an inline 100vh height; on iOS Safari that is the large
    // viewport, so the drawer's bottom row ends up under the browser's
    // bottom bar. dvh follows the visible viewport (Chrome < 108 keeps vh).
    @supports (height: 100dvh) {
      &.v-navigation-drawer {
        height: 100dvh !important;
      }
    }

    .drawer-body {
      @apply tw-flex tw-min-h-full tw-flex-col tw-pb-3;

      // Room for the GPTHelp bubble (fixed bottom-left) at the end of the
      // scroll while it shows, so it never covers the last row.
      &.has-bubble-room {
        @apply tw-pb-[72px];
      }
    }

    .drawer-footer {
      @apply tw-mt-auto tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-pt-3;
    }

    // Mobile browsers reserve the bottom edge of the screen for their own
    // chrome (iOS Safari re-expands its toolbar when that strip is tapped).
    @supports (padding: max(0px, 1px)) {
      @media (pointer: coarse) {
        .drawer-body {
          padding-bottom: max(env(safe-area-inset-bottom), 12px);
        }

        .drawer-body.has-bubble-room {
          padding-bottom: max(env(safe-area-inset-bottom), 72px);
        }
      }
    }
  }
</style>
